import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Autocomplete, Box, Button, CircularProgress, Grid, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import { Controller ,useForm,useFormContext} from 'react-hook-form';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import parse from 'autosuggest-highlight/parse';
import { debounce } from '@mui/material/utils';
import { Directions, Search, TurnRight } from '@mui/icons-material';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

function loadScript(src, position, id) {
  if (!position) {
    return;
  }

  const script = document.createElement('script');
  script.setAttribute('async', '');
  script.setAttribute('id', id);
  script.src = src;
  position.appendChild(script);
}

const CoordinateDetails = (props) => {
  // const {addressMode} =props;
  const { watch,control, setValue, formState: { errors } } = useFormContext();
  // const addressMode = watch('addressMode');
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [googleServicesReady, setGoogleServicesReady] = useState(false);
  const loaded = useRef(false);

  // Load Google Maps script
  useEffect(() => {
    if (typeof window !== 'undefined' && !loaded.current) {
      if (!document.querySelector('#google-maps')) {
        loadScript(
          `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`,
          document.querySelector('head'),
          'google-maps',
        );
      }
      loaded.current = true;
    }
  }, []);

  // Check if Google services are ready
  useEffect(() => {
    const checkGoogleServices = setInterval(() => {
      if (window.google && window.google.maps && window.google.maps.places) {
        setGoogleServicesReady(true);
        clearInterval(checkGoogleServices);
      }
    }, 100);

    return () => clearInterval(checkGoogleServices);
  }, []);

  const services = useMemo(() => {
    if (!googleServicesReady) return { autocomplete: null, places: null };
    
    return {
      autocomplete: new window.google.maps.places.AutocompleteService(),
      places: new window.google.maps.places.PlacesService(document.createElement('div'))
    };
  }, [googleServicesReady]);

  const fetch = useMemo(
    () =>
      debounce((request, callback) => {
        if (services.autocomplete) {
          services.autocomplete.getPlacePredictions(request, callback);
        }
      }, 400),
    [services.autocomplete],
  );

  useEffect(() => {
    let active = true;

    if (inputValue === '') {
      setOptions([]);
      return undefined;
    }

    if (!services.autocomplete) {
      return undefined;
    }

    setLoading(true);

    fetch({ input: inputValue }, (results) => {
      if (active) {
        setOptions(results || []);
        setLoading(false);
      }
    });

    return () => {
      active = false;
    };
  }, [inputValue, fetch, services.autocomplete]);

  const handlePlaceSelect = (place) => {
    if (!place || !place.place_id || !services.places) return;

    services.places.getDetails(
      { placeId: place.place_id, fields: ['address_components', 'geometry', 'formatted_address'] },
      (placeResult, status) => {
        console.log("placeResult",placeResult);
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          const addressComponents = placeResult.address_components;
          const lat = placeResult.geometry.location.lat();
          const lng = placeResult.geometry.location.lng();

          setValue(`address`, placeResult.formatted_address,{shouldValidate:true});
          setValue(`latitude`, lat.toString(),{shouldValidate:true});
          setValue(`longitude`, lng.toString(),{shouldValidate:true});
          setValue(`houseFlatBlockNo`, addressComponents.find(c => c.types.includes('premise'))?.long_name || '',{shouldValidate:true});
          setValue(`apartmentRoadArea`, addressComponents.find(c => c.types.includes('route'))?.long_name || '',{shouldValidate:true});
          setValue(`landmark`, addressComponents.find(c => c.types.includes('landmark'))?.long_name || '',{shouldValidate:true});
          setValue(`state`, addressComponents.find(c => c.types.includes('administrative_area_level_1'))?.long_name || '',{shouldValidate:true});
          setValue(`city`, addressComponents.find(c => c.types.includes('locality'))?.long_name || '',{shouldValidate:true});
          setValue(`pincode`, addressComponents.find(c => c.types.includes('postal_code'))?.long_name || '',{shouldValidate:true});
        }
      }
    );
  };

  return (
    <Box sx={{p:'1% 5%'}}>
      <Grid item xs={10} sx={{display:'flex',gap:2}}>
        <Controller
          name={`address-search`}
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Autocomplete
            sx={{ 
              width: '100%',
              '& .MuiOutlinedInput-root': {
                paddingRight: '5px',
              }
            }}
              fullWidth
              id={`address-autocomplete`}
              // sx={{ width: '100%' }}
              size='small'
              // disabled={addressMode == 'select_address'}
              getOptionLabel={(option) =>
                typeof option === 'string' ? option : option.description
              }
              filterOptions={(x) => x}
              options={options}
              autoComplete
              includeInputInList
              filterSelectedOptions
              value={value}
              onChange={(event, newValue) => {
                onChange(newValue ? newValue.description : '');
                handlePlaceSelect(newValue);
              }}
              onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder='Search Address in google maps'
                  fullWidth
                  error={!!error}
                
                  // helperText={error?.message}
                  InputProps={{
                    ...params.InputProps,
                    style: { fontSize: '0.75rem',color:'#000',paddingRight:'1rem' },
                    endAdornment: (
                      <Box sx={{display:'flex',gap:1}}>
                        <Search sx={{fontSize:'1.25rem'}} color="action"/>
                        <Directions sx={{fontSize:'1.25rem'}} color="primary"/>
                      </Box>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => {
                const matches = option.structured_formatting.main_text_matched_substrings || [];
                const parts = parse(
                  option.structured_formatting.main_text,
                  matches.map((match) => [match.offset, match.offset + match.length]),
                );

                return (
                  <li {...props}>
                    <Grid container alignItems="center">
                      <Grid item>
                        <Box component={LocationOnIcon} sx={{ color: 'text.secondary', mr: 2 }} />
                      </Grid>
                      <Grid item xs>
                        {parts.map((part, index) => (
                          <span
                            key={index}
                            style={{
                              fontWeight: part.highlight ? 700 : 400,
                            }}
                          >
                            {part.text}
                          </span>
                        ))}
                        <Typography variant="body2" color="text.secondary">
                          {option.structured_formatting.secondary_text}
                        </Typography>
                      </Grid>
                    </Grid>
                  </li>
                );
              }}
              noOptionsText={<span style={{ fontSize: '0.75rem' }}>{watch('address-search')? 'Address not found in google map':'enter address to search'}</span>}

            />
          )}
        />
      </Grid>
    </Box>
  );
}

export default CoordinateDetails;
